import { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import api from '../../../api/client';

function measureAlias(m) {
  return m.alias || `${m.agg}_${m.field}`;
}

export default function AskPage() {
  const [question, setQuestion] = useState('');
  const [answer,   setAnswer]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  async function handleAsk(e) {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setAnswer(null);
    try {
      const { data } = await api.post('/intelligence/ask', { question });
      setAnswer(data.data || null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }

  const ok       = answer && answer.status === 'ok';
  const plan     = ok ? answer.plan : null;
  const columns  = ok ? (answer.data?.columns || []) : [];
  const rows     = ok ? (answer.data?.rows || []) : [];
  const dims     = plan?.dims || [];
  const measures = plan?.measures || [];
  const showChart = dims.length >= 1 && measures.length >= 1 && rows.length >= 1;
  const xKey     = dims[0];
  const yKey     = measures[0] ? measureAlias(measures[0]) : null;

  // Narrative for ok; refusal/error reason otherwise — both shown gracefully.
  const narrative = answer
    ? (answer.status === 'ok' ? answer.narrative : answer.reason)
    : null;

  return (
    <div data-testid="intel-ask-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Ask</h2>
      <p style={{ color: '#666' }}>Ask a question about your data in plain language.</p>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      <form onSubmit={handleAsk} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          data-testid="intel-ask-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. headcount by department"
          style={{ flex: 1, minWidth: 280, padding: '0.4rem 0.6rem' }}
        />
        <button type="submit" data-testid="intel-ask-submit" disabled={loading || !question.trim()}>
          {loading ? 'Asking...' : 'Ask'}
        </button>
      </form>

      {answer && (
        <>
          <div
            data-testid="intel-ask-answer"
            style={{
              border: '1px solid #ccc',
              borderRadius: 8,
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              background: answer.status === 'ok' ? '#f6faf6' : '#fdf3f3',
            }}
          >
            {narrative || (answer.status === 'ok' ? 'No narrative.' : 'The question could not be answered.')}
          </div>

          {ok && (
            <>
              {showChart && (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={rows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={xKey} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey={yKey} fill="#16a34a" />
                  </BarChart>
                </ResponsiveContainer>
              )}

              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: showChart ? '1rem' : 0 }}>
                <thead>
                  <tr>
                    {columns.map((c) => <th key={c} style={{ textAlign: 'left' }}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} data-testid="intel-ask-row">
                      {columns.map((c) => <td key={c}>{String(r[c] ?? '')}</td>)}
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan={columns.length || 1}>No rows matched.</td></tr>}
                </tbody>
              </table>

              <h4 style={{ marginBottom: '0.25rem' }}>Generated plan</h4>
              <pre
                data-testid="intel-ask-plan"
                style={{ background: '#f4f4f4', padding: '0.75rem', borderRadius: 6, overflowX: 'auto' }}
              >
                {JSON.stringify(plan, null, 2)}
              </pre>
            </>
          )}
        </>
      )}
    </div>
  );
}
